import { IGame } from "../types/game";
import { FlashLineState } from "./flash-line-state";
import GameState from "./game-state";

export default class CheckLineState extends GameState {
  holePos: boolean[][] = new Array(36);
  private clearedRows: number[] = [];

  constructor(game: IGame) {
    super(game);

    for (let i = 0; i < this.holePos.length; i++) {
      this.holePos[i] = new Array(18).fill(false);
    }
    this.clearedRows = [];
  }

  enter(): void {
    for (let i = 0; i < this.holePos.length; i++) {
      if (!this.holePos[i]) {
        this.holePos[i] = new Array(18).fill(false);
      } else {
        this.holePos[i].fill(false);
      }
    }
    this.clearedRows = [];
  }

  update(frameTime: number): void {
    super.update(frameTime);

    let count: number = this.checkNbLine();

    if (count) {
      // Decrement attack counts when lines are cleared (matches original Player_stamp::step() lines 1446-1451)
      // In survivor mode, don't clear last_attacker when attacks reach 0
      this.decrementAttackCounts();

      this.canvas.depth += count;
      this.canvas.complexity++;
      this.checkClean();

      // Use call() instead of pushState() to wait for FlashLine→CheckLink to complete
      // This matches original Player_check_line::step() line 535: call(new Player_flash_lines(canvas))
      this.call(new FlashLineState(this.game));
      
      // Return WITHOUT popping - when FlashLine/CheckLink finish, this state will run again
      // and check for more lines (chains/combos). This creates the recursive line-checking loop.
      return;
    }
    
    // No lines found - send attacks and finish
    if (this.canvas.idle < 2) {
      const linesToSend = this.canvas.depth > 0 ? this.canvas.depth - 1 : 0;
      for (let j = 0; j < 36; j++) {
        if (!this.canvas.moved[j]) {
          this.canvas.moved[j] = new Array(18).fill(false);
        } else {
          for (let i = 0; i < 18; i++) {
            this.canvas.moved[j][i] = false;
          }
        }
      }
      for (let j = 0; j < linesToSend && j < 36; j++) {
        if (this.holePos[j]) {
          for (let i = 0; i < 18; i++) {
            this.canvas.moved[j][i] = !!this.holePos[j][i];
          }
        } else {
          for (let i = 0; i < 18; i++) {
            this.canvas.moved[j][i] = false;
          }
        }
      }
      this.game.giveLine();
    } else {
      this.canvas.depth = 0;
      this.canvas.complexity = 0;
      this.canvas.sendForClean = false;
    }

    // Only pop when no lines found (matches original line 550: ret() in else branch)
    this.game.popState(this);
  }

  /*
   * Check if the line is full occupied, then
   * clear these lines and fix top and bottom links
   * to these blocks
   */
  private checkNbLine(): number {
    let count: number = 0;
    this.clearedRows = [];
    for (let row = 12; row < 32; row++) {
      let col = 4;
      for (; col < 14; col++) {
        if (!this.canvas.occupied[row][col]) {
          break;
        }
      }

      if (col !== 14) continue;

      this.clearedRows.push(row);

      for (let col = 4; col < 14; col++) {
        this.clearTopLinks(row, col);

        this.setHolePos(row, col, count);

        this.clearBlock(row, col);

        this.clearBottomLinks(row, col);
      }
      count++;
    }
    return count;
  }

  private clearBottomLinks(row: number, col: number) {
    if (
      this.canvas.occupied[row + 1][col] &&
      !(this.canvas.blocks[row + 1][col] & 2)
    ) {
      this.canvas.blocks[row + 1][col] |= 2;
      this.canvas.dirted[row + 1][col] = 2;
      this.canvas.flashBlocks[row][col] |= 8;
    }
  }

  private clearTopLinks(row: number, col: number) {
    if (
      this.canvas.occupied[row - 1][col] &&
      !(this.canvas.blocks[row - 1][col] & 8)
    ) {
      this.canvas.blocks[row - 1][col] |= 8;
      this.canvas.dirted[row - 1][col] = 2;
      this.canvas.flashBlocks[row][col] |= 2;
    }
  }

  private clearBlock(row: number, col: number) {
    this.canvas.flashBlocks[row][col] = this.canvas.blocks[row][col];
    this.canvas.blocks[row][col] = 0;
    this.canvas.occupied[row][col] = false;
    this.canvas.blinded[row][col] = 0;
    this.canvas.bflash[row][col] = 0;
    this.canvas.dirted[row][col] = 2;
  }

  private setHolePos(row: number, col: number, count: number) {
    this.holePos[this.canvas.depth + count][col] = !!(
      this.canvas.moved[row] && this.canvas.moved[row][col]
    );
  }

  /* Win points for clean canvas */
  private checkClean() {
    for (let j = 12; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        if (this.canvas.occupied[j][i]) {
          return;
        }
      }
    }
    this.canvas.sendForClean = true;
  }

  /**
   * Decrement attack counts when lines are cleared (matches original Player_stamp::step() lines 1446-1451)
   * In survivor mode, don't clear last_attacker when attacks reach 0
   */
  private decrementAttackCounts(): void {
    const gameMode = this.game.getGameMode?.();
    const isSurvivorMode = gameMode === "survivor";

    // Decrement all attack counts by 1 (matches original: canvas->attacks[i]--)
    // Note: attackCounts is now keyed by numPlayer (0-5), matching original attacks[MAXPLAYERS]
    for (const numPlayerStr in this.canvas.attackCounts) {
      const numPlayer = parseInt(numPlayerStr, 10);
      if (isNaN(numPlayer) || numPlayer < 0 || numPlayer > 5) continue;
      
      if (this.canvas.attackCounts[numPlayer] > 0) {
        this.canvas.attackCounts[numPlayer]--;
        
        // If attack count reaches 0 and it was the last_attacker, clear it (unless survivor mode)
        if (
          this.canvas.attackCounts[numPlayer] === 0 &&
          this.canvas.lastAttackerNumPlayer === numPlayer &&
          !isSurvivorMode
        ) {
          // In non-survivor mode, clear last_attacker when attacks reach 0
          // In survivor mode, preserve last_attacker (matches original line 1450-1451)
          this.canvas.lastAttackerNumPlayer = undefined;
        }
      }
    }
  }

  toString(): string {
    return "Line Check";
  }
}
